import classNames from 'clsx';
import format from 'date-fns/format';
import { ReactComponent as LogoCloud } from 'img/icons/logo_cloud.svg';
import React, { type ReactElement, memo, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { APP_TITLE, APP_WHITE_LOGO, ENABLE_FULL_RESPONSIVE, IS_B2B_BIZ_USER, IS_MY_TEAM } from 'reactApp/appHelpers/configHelpers';
import { allAttachesBttn as allAttachesBttnFeature } from 'reactApp/appHelpers/featuresHelpers/features/allAttachesBttn';
import { ANTIVIRUS_BRAND } from 'reactApp/appHelpers/featuresHelpers/features/antivirusBrand';
import { DISABLE_LOGO_CLICK_ATTACH_VIEWER } from 'reactApp/appHelpers/featuresHelpers/features/mailSuperapp';
import { getIsViewerFileVerifiedByKaspersky } from 'reactApp/modules/file/utils';
import { Hint } from 'reactApp/ui/Hint/Hint';
import { NameComponent } from 'reactApp/ui/Name/Name';
import { ProtectedHint } from 'reactApp/ui/ProtectedHint/ProtectedHint';
import { AllAttachesButton } from 'reactApp/ui/ReactViewer/AttachInfo/components/allAttachesButton';
import { MobileAppPromoTooltip } from 'reactApp/ui/ReactViewer/ViewerHeader/MobileAppPromoTooltip';
import opener from 'reactApp/utils/opener';

import styles from './AttachInfo.css';

interface Props {
    name: string;
    author: string;
    fileName: string;
    fileExt: string;
    size: string;
    timestamp: number;
    isTemporary: boolean;

    onClick(): void;

    forceActive({ isActive: boolean }): void;
}

const handleCloudLogoClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();

    if (DISABLE_LOGO_CLICK_ATTACH_VIEWER) {
        return;
    }

    opener('/home', true);
};

const hintAntivirusText = `Файл проверен антивирусом${ANTIVIRUS_BRAND === 'default' ? '' : ` ${ANTIVIRUS_BRAND}`}`;

export const AttachInfo = memo(
    ({ name, author, timestamp, onClick, fileName, fileExt, size, isTemporary, forceActive }: Props): ReactElement | null => {
        const fileTitle = `${fileName}.${fileExt}`;
        const hintText = IS_B2B_BIZ_USER ? APP_TITLE : 'Облако Mail';
        const logoRef = useRef<HTMLDivElement>(null);

        const isViewerFileVerifiedByKaspersky = useSelector(getIsViewerFileVerifiedByKaspersky);

        const myTeamAttachInfo = useMemo(() => {
            return [name, fileTitle, size].filter(Boolean).join(', ');
        }, [name, fileTitle, size]);

        return (
            <div
                className={classNames(styles.root, {
                    [styles.rootClickable]: !isTemporary && !IS_MY_TEAM,
                    [styles.responsive]: ENABLE_FULL_RESPONSIVE,
                })}
                onClick={IS_MY_TEAM ? undefined : onClick}
            >
                <MobileAppPromoTooltip elementRef={logoRef} />
                {!IS_MY_TEAM && (
                    <>
                        {IS_B2B_BIZ_USER ? (
                            <Hint theme="dark" text={hintText}>
                                <img className={styles.logoB2B} src={APP_WHITE_LOGO} />
                            </Hint>
                        ) : (
                            <div className={styles.iconWrapper} onClick={handleCloudLogoClick} ref={logoRef}>
                                <Hint theme="dark" text={hintText}>
                                    <LogoCloud className={styles.newLogo} />
                                </Hint>
                            </div>
                        )}
                        <div className={styles.divider} />
                    </>
                )}
                <div className={styles.attachBlock}>
                    {!IS_MY_TEAM ? (
                        <div className={styles.attachTopBlock}>
                            <div className={styles.author}>{Boolean(author) && author}</div>
                            <div className={styles.attachTime}>{Boolean(timestamp) && format(timestamp, 'dd.MM.yy, HH:mm')}</div>
                        </div>
                    ) : (
                        <div className={styles.attachTeamTopBlock}>
                            {Boolean(author) && author}
                            <span className={styles.attachTime}>{Boolean(timestamp) && format(timestamp, 'dd.MM.yy, HH:mm')}</span>
                        </div>
                    )}
                    {!IS_MY_TEAM ? (
                        <div className={styles.attachBottomBlock}>
                            {name && (
                                <Hint text={name} theme="dark" showDelay>
                                    <div className={styles.attachName}>{name}</div>
                                </Hint>
                            )}
                            {fileTitle && (
                                <Hint text={fileTitle} theme="dark" showDelay>
                                    <div className={classNames({ [styles.attachItemPrefix]: name })}>
                                        <NameComponent name={fileName} extension={fileExt} truncate />
                                    </div>
                                </Hint>
                            )}
                            {size && (
                                <div
                                    data-qa-id="attach-info-size"
                                    className={classNames({
                                        [styles.attachItemPrefix]: fileTitle && !allAttachesBttnFeature,
                                        [styles.attachItemSize]: Boolean(allAttachesBttnFeature),
                                    })}
                                >
                                    {size}
                                </div>
                            )}
                            <AllAttachesButton forceActive={forceActive} />
                            {isViewerFileVerifiedByKaspersky && <ProtectedHint text={hintAntivirusText} />}
                        </div>
                    ) : (
                        <div className={styles.attachTeamBottomBlock}>
                            <Hint text={myTeamAttachInfo} theme="dark">
                                <>{myTeamAttachInfo}</>
                            </Hint>
                        </div>
                    )}
                </div>
            </div>
        );
    }
);

AttachInfo.displayName = 'AttachInfo';

AttachInfo.whyDidYouRender = true;
